/**
 * LazyLoad — динамическая подгрузка script/style по требованию.
 *
 * Решает проблему cold-start: на 1-й экран (view-home) нужны
 * ~150 КБ JS + ~110 КБ CSS. Раньше в <head> загружалось ~673 KB JS +
 * 237 KB CSS (всё что когда-либо может понадобиться). При первом
 * заходе в view-profile/view-topup/etc. — соответствующий бандл
 * подгружается dynamic'ом, кэшируется по URL чтобы повторный
 * switchView не дёргал сеть.
 *
 * API:
 *   await LazyLoad.script('js/foo.js')                       → classic <script>
 *   await LazyLoad.script('js/tiptap.js', { type: 'module'}) → <script type=module>
 *   await LazyLoad.style('css/bar.css')                      → <link rel=stylesheet>
 *   await LazyLoad.bundle([
 *     'js/a.js',                              // classic
 *     'css/b.css',
 *     { url: 'js/tiptap.js', type: 'module' } // ES module
 *   ])
 *
 * Когда нужен `type: 'module'`:
 *   Если файл использует ТОП-УРОВНЕВЫЙ `import ...` (static ES imports).
 *   Без `type=module` браузер падает с SyntaxError на первом import,
 *   скрипт не выполняется, window.X-экспорт никогда не появляется.
 *   Грабли — см. инцидент 2026-05-17 (tiptap-init.js).
 *
 * Безопасность:
 *   - Каждый URL грузится РОВНО ОДИН РАЗ за сессию (cache в Map).
 *   - Параллельные вызовы возвращают тот же Promise.
 *   - Сохраняет порядок в DOM (appendChild в <head>).
 *   - При network-error rejected Promise НЕ кэшируется — next call retry.
 *   - Cache-bust: текущий window.APP_VERSION (фиксируется в index.html)
 *     добавляется как ?v= автоматически если src не содержит query.
 */
(function () {
    'use strict';

    const cache = new Map();  // url/type → one native resource, with bounded waits
    const ASSET_LOAD_TIMEOUT_MS = 15000;

    function waitFor(entry) {
        if (entry.state === 'loaded') return entry.ready;
        if (entry.wait) return entry.wait;
        // A removed <script> may still execute when its response arrives.
        // On timeout retain that single native request, but release its wait.
        // Retry can observe a late success without executing the module twice.
        entry.wait = new Promise((resolve, reject) => {
            entry.resolve = resolve;
            entry.reject = reject;
            entry.timer = setTimeout(() => {
                entry.wait = null;
                entry.resolve = entry.reject = null;
                reject(new Error(`Timed out loading asset: ${entry.url}`));
            }, ASSET_LOAD_TIMEOUT_MS);
        });
        return entry.wait;
    }

    function loadAsset(url, type) {
        const key = type === 'module' ? `module:${url}` : url;
        if (cache.has(key)) return waitFor(cache.get(key));
        const style = type === 'style';
        const el = document.createElement(style ? 'link' : 'script');
        if (style) { el.rel = 'stylesheet'; el.href = url; }
        else {
            el.src = url;
            if (type === 'module') el.type = 'module';
            else el.async = false;
        }
        const entry = { url, state: 'loading', wait: null, ready: Promise.resolve() };
        cache.set(key, entry);
        const complete = (error) => {
            if (entry.state !== 'loading') return;
            entry.state = error ? 'failed' : 'loaded';
            clearTimeout(entry.timer);
            el.onload = el.onerror = null;
            const settle = error ? entry.reject : entry.resolve;
            entry.wait = entry.resolve = entry.reject = null;
            if (error) {
                if (cache.get(key) === entry) cache.delete(key);
                el.remove();
            }
            settle?.(error);
        };
        el.onload = () => complete();
        el.onerror = () => complete(new Error(`Failed to load asset: ${url}`));
        const pending = waitFor(entry);
        try { document.head.appendChild(el); } catch (error) { complete(error); }
        return pending;
    }

    /**
     * Cache-bust query. Приоритет:
     *   1. Per-file hash из window.APP_FILE_VERSIONS (инжектится бэком,
     *      содержит mtime-hash на каждый js/css). Только изменившиеся
     *      файлы получают новый ?v= → юзер ре-качает ТОЛЬКО их,
     *      остальное берётся из browser cache (immutable, 1y).
     *   2. window.APP_VERSION (git SHA) — fallback для файлов которых
     *      нет в map'е (например только что добавлен), и для legacy.
     *   3. Если ни того ни другого — URL без ?v= (dev режим без бэка).
     */
    function withVersion(url) {
        if (url.includes('?')) return url;
        // Нормализуем относительные пути — APP_FILE_VERSIONS хранит
        // ключи как 'js/foo.js' / 'css/bar.css'.
        const key = url.replace(/^\/+/, '');
        const fileHash = window.APP_FILE_VERSIONS?.[key];
        const version = fileHash || window.APP_VERSION;
        return version ? `${url}?v=${version}` : url;
    }

    function loadScript(src, opts = {}) {
        return loadAsset(withVersion(src), opts.type === 'module' ? 'module' : 'script');
    }

    function loadStyle(href) {
        return loadAsset(withVersion(href), 'style');
    }

    function loadEntry(entry) {
        // Entry — либо строка (url), либо {url, type}.
        if (typeof entry === 'string') {
            if (entry.endsWith('.css')) return loadStyle(entry);
            if (entry.endsWith('.js')) return loadScript(entry);
            return Promise.reject(new Error(`Unknown asset type: ${entry}`));
        }
        if (entry && typeof entry === 'object' && entry.url) {
            if (entry.url.endsWith('.css')) return loadStyle(entry.url);
            if (entry.url.endsWith('.js')) return loadScript(entry.url, { type: entry.type });
            return Promise.reject(new Error(`Unknown asset type: ${entry.url}`));
        }
        return Promise.reject(new Error(`Invalid bundle entry: ${JSON.stringify(entry)}`));
    }

    async function loadBundle(entries) {
        // Загружаем параллельно, classic-скрипты держат порядок через async=false.
        await Promise.all(entries.map(loadEntry));
    }

    window.LazyLoad = {
        script: loadScript,
        style: loadStyle,
        bundle: loadBundle,
    };

    console.log('[LazyLoad] ✅ Initialized');
})();
